// A small card shown when hovering a link in a note: what the link points at.
import { type LinkTarget, typeLabel } from '@modelviewer/core';
import { FileText, GitCommitVertical, Layers, PenLine, Table2 } from 'lucide-react';
import { migrationDate } from '../views/Explorer';
import { findSet, refKey, useWorkspace } from '../state/workspace';

export function KindIcon({ kind, size = 13 }: { kind: LinkTarget['kind'] | 'unresolved'; size?: number }) {
  if (kind === 'note') return <FileText size={size} />;
  if (kind === 'sketch') return <PenLine size={size} />;
  if (kind === 'migration') return <GitCommitVertical size={size} />;
  if (kind === 'context') return <Layers size={size} />;
  return <Table2 size={size} />;
}

function EntityPreview({ target }: { target: Extract<LinkTarget, { kind: 'entity' }> }) {
  const model = useWorkspace((s) => s.models[refKey(target.ref)]);
  const entity = model?.result.doc.model.entities[target.entity];
  if (!entity) return <p className="text-muted">Not loaded yet.</p>;
  const rows = Object.entries(entity.properties);
  return (
    <>
      <p className="mb-1 text-[11px] text-faint">{target.model}</p>
      <ul className="font-mono text-[11.5px]">
        {rows.slice(0, 12).map(([name, p]) => (
          <li key={name} className={`flex gap-3 ${name === target.property ? 'text-accent' : ''}`}>
            <span className="min-w-0 flex-1 truncate">{name}</span>
            <span className="text-muted">{typeLabel(p)}</span>
          </li>
        ))}
        {rows.length > 12 && <li className="text-faint">and {rows.length - 12} more</li>}
      </ul>
    </>
  );
}

function Body({ target }: { target: LinkTarget }) {
  const notes = useWorkspace((s) => s.notes);
  const sketches = useWorkspace((s) => s.sketches);
  switch (target.kind) {
    case 'entity':
      return <EntityPreview target={target} />;
    case 'note': {
      const body = notes[target.id]?.body ?? '';
      return <p className="line-clamp-6 whitespace-pre-wrap text-muted">{body.slice(0, 400) || 'Empty note.'}</p>;
    }
    case 'sketch': {
      const s = sketches[target.id];
      return (
        <p className="text-muted">
          {s?.doc.useCase ?? s?.doc.description ?? 'A sketch.'} {s && <span className="text-faint">· {Object.keys(s.doc.model.entities).length} entities</span>}
        </p>
      );
    }
    case 'migration':
      return (
        <p className="text-muted">
          Migration of {target.context}, {migrationDate(target.id.slice(0, 14))}
        </p>
      );
    case 'context':
      return <p className="text-muted">{findSet(target.setKey)?.migrations.length ?? 0} migrations</p>;
  }
}

export function LinkPreview({ target, x, y }: { target: LinkTarget | 'unresolved'; x: number; y: number }) {
  return (
    <div
      role="tooltip"
      className="pointer-events-none fixed z-50 w-72 rounded-lg border border-border-strong bg-panel-2 p-2.5 text-[12.5px] shadow-[var(--shadow-pop)]"
      style={{ left: Math.min(x, window.innerWidth - 300), top: y + 18 }}
    >
      {target === 'unresolved' ? (
        <p className="text-muted">Nothing has this name yet. Click to create a note.</p>
      ) : (
        <>
          <p className="mb-1 flex items-center gap-1.5 font-semibold">
            <span className="text-faint">
              <KindIcon kind={target.kind} />
            </span>
            {target.kind === 'entity' ? (target.property ? `${target.entity}.${target.property}` : target.entity) : target.kind === 'note' || target.kind === 'sketch' ? target.title : target.name}
          </p>
          <Body target={target} />
        </>
      )}
    </div>
  );
}
